import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { OSM_ATTRIBUTION } from '../lib/legal'
import { placeLabels, TONE_COLOR } from '../lib/fleetCore'

const CSS = `
.hcfm-map.leaflet-container{font-family:DM Sans,system-ui,sans-serif;background:#0b1526}
.hcfm-map .leaflet-bar{border:1px solid rgba(255,255,255,.1)!important;box-shadow:0 4px 14px rgba(0,0,0,.35)}
.hcfm-map .leaflet-bar a,.hcfm-map .hcfm-fit{background:#101c31!important;color:#e7edf6!important;border-color:rgba(255,255,255,.1)!important}
.hcfm-map .leaflet-bar a:hover,.hcfm-map .hcfm-fit:hover{background:#18284a!important}
.hcfm-map .leaflet-control-layers{background:#101c31;color:#e7edf6;border:1px solid rgba(255,255,255,.1);border-radius:10px;box-shadow:0 4px 14px rgba(0,0,0,.35)}
.hcfm-map .leaflet-control-layers-toggle{filter:invert(1) hue-rotate(180deg)}
.hcfm-map .leaflet-control-attribution{background:rgba(10,19,34,.75);color:#6f82a0;font-size:9.5px}
.hcfm-map .leaflet-control-attribution a{color:#8fa1bb}
.hcfm-icon{background:none;border:none}
.hc-content .hcfm-map *{max-width:none!important}
.hc-content .hcfm-map div.hcfm-icon{width:34px!important;min-width:34px!important;flex:none!important}
.hcfm-wrap{position:relative;width:34px;height:34px;transition:transform .15s ease}
.hcfm-dot{position:absolute;inset:0;border-radius:50%;background:linear-gradient(135deg,#3b7bff,#1d4ed8);border:2px solid var(--hcfm-c);box-shadow:0 0 0 4px rgba(47,107,255,.22),0 4px 12px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;box-sizing:border-box}
.hcfm-pulse{position:absolute;inset:0;border-radius:50%;background:#3b7bff;opacity:0}
.hcfm-moving .hcfm-pulse{animation:hcfmPulse 2s ease-out infinite}
@keyframes hcfmPulse{0%{transform:scale(1);opacity:.6}100%{transform:scale(2.6);opacity:0}}
.hcfm-label{position:absolute;left:42px;top:50%;transform:translateY(-50%);transition:left .15s,top .15s,transform .15s;white-space:nowrap;background:rgba(12,22,39,.94);border:1px solid rgba(255,255,255,.12);color:#e7edf6;padding:6px 10px;border-radius:9px;box-shadow:0 6px 18px rgba(0,0,0,.45);pointer-events:none}
.hcfm-n{font:700 12px/1.2 DM Sans,system-ui,sans-serif}
.hcfm-s{font:500 10.5px/1.2 DM Sans,system-ui,sans-serif;color:#8fa1bb;margin-top:3px;display:flex;align-items:center;gap:5px}
.hcfm-s i{width:6px;height:6px;border-radius:50%;display:inline-block}
.hcfm-side-left .hcfm-label{left:auto;right:42px}
.hcfm-side-up .hcfm-label{left:50%;top:auto;bottom:42px;transform:translateX(-50%)}
.hcfm-side-down .hcfm-label{left:50%;top:42px;transform:translateX(-50%)}
.hcfm-hl .hcfm-wrap{transform:scale(1.18)}
.hcfm-hl .hcfm-label{border-color:#c9a84c}
.hcfm-popup .leaflet-popup-content-wrapper{background:#101c31;color:#e7edf6;border:1px solid rgba(255,255,255,.1);border-radius:12px;padding:0;overflow:hidden;box-shadow:0 12px 32px rgba(0,0,0,.5)}
.hcfm-popup .leaflet-popup-tip{background:#101c31}
.hcfm-popup .leaflet-popup-content{margin:0;min-width:230px;font-family:DM Sans,system-ui,sans-serif}
.hcfm-pop-h{padding:11px 14px 8px;font-weight:700;font-size:13.5px;display:flex;justify-content:space-between;align-items:center;gap:10px}
.hcfm-chip{font-size:10px;font-weight:700;padding:2px 8px;border-radius:10px;white-space:nowrap}
.hcfm-pop-b{padding:0 14px 10px;font-size:12px;color:#8fa1bb;line-height:1.75}
.hcfm-pop-b b{color:#e7edf6;font-variant-numeric:tabular-nums;font-weight:600}
.hcfm-open{display:block;width:100%;border:none;border-top:1px solid rgba(255,255,255,.08);background:transparent;color:#6aa0ff;font-weight:700;font-size:12.5px;padding:9px;cursor:pointer;font-family:inherit}
.hcfm-open:hover{background:rgba(47,107,255,.12)}
.hcfm-fit{border:none;width:30px;height:30px;cursor:pointer;display:flex;align-items:center;justify-content:center}
`
function ensureCss() {
  if (typeof document === 'undefined') return
  let s = document.getElementById('hc-fleetmap-css')
  if (!s) { s = document.createElement('style'); s.id = 'hc-fleetmap-css'; document.head.appendChild(s) }
  s.textContent = CSS
}

function dm(v, pos, neg, degPad) {
  const h = v >= 0 ? pos : neg
  const a = Math.abs(v)
  let d = Math.floor(a)
  let m = ((a - d) * 60).toFixed(1)
  if (m === '60.0') { d += 1; m = '0.0' }
  return String(d).padStart(degPad, '0') + '\u00b0' + m.padStart(4, '0') + '\u2032' + h
}
export const fmtLat = lat => (typeof lat === 'number' ? dm(lat, 'N', 'S', 2) : '')
export const fmtLon = lon => (typeof lon === 'number' ? dm(lon, 'E', 'W', 3) : '')
export function ago(ts) {
  if (!ts) return ''
  const m = Math.round((Date.now() - new Date(ts).getTime()) / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return m + ' min ago'
  const h = Math.round(m / 60)
  if (h < 48) return h + ' h ago'
  return Math.round(h / 24) + ' d ago'
}
const esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const statusColor = t => (t == null ? '#6f82a0' : t > 0 ? '#e5484d' : '#35c98b')
// the marker's ring: the readiness colour when known (fleet-1), else red / green from the count
const ringOf = v => (v.tone && TONE_COLOR[v.tone]) || statusColor(v.total)
const isMoving = v => Number(v.speed || 0) > 0.5
const validCourse = c => typeof c === 'number' && c >= 0 && c < 360

function markerHtml(v) {
  const moving = isMoving(v)
  const rot = moving && validCourse(v.course) ? v.course : 0
  const glyph = moving
    ? `<svg viewBox="0 0 24 24" width="16" height="16" style="transform:rotate(${rot}deg)"><path d="M12 2 L18.5 20 L12 15.8 L5.5 20 Z" fill="#fff"/></svg>`
    : `<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 15h18l-2.6 4.5H5.6z"/><path d="M6 15l1.5-4h8.5l2 4"/><path d="M9.5 11V8h4l1 3"/></svg>`
  const sub = moving ? 'Underway \u00b7 ' + Number(v.speed).toFixed(1) + ' kn' : 'Stationary'
  return `<div class="hcfm-wrap${moving ? ' hcfm-moving' : ''}" style="--hcfm-c:${ringOf(v)}"><div class="hcfm-pulse"></div><div class="hcfm-dot">${glyph}</div><div class="hcfm-label"><div class="hcfm-n">${esc(v.vessel_name || 'Vessel')}</div><div class="hcfm-s"><i style="background:${moving ? '#35c98b' : '#3b7bff'}"></i>${sub}</div></div></div>`
}

function popupHtml(v) {
  const t = v.total
  const chip = t == null
    ? `<span class="hcfm-chip" style="background:rgba(143,161,187,.18);color:#8fa1bb">Checking\u2026</span>`
    : t > 0
      ? `<span class="hcfm-chip" style="background:rgba(229,72,77,.18);color:#ff7b7f">${t} to action</span>`
      : `<span class="hcfm-chip" style="background:rgba(53,201,139,.16);color:#35c98b">All good</span>`
  const motion = isMoving(v)
    ? `Underway ${Number(v.speed).toFixed(1)} kn${validCourse(v.course) ? ' \u00b7 ' + Math.round(v.course) + '\u00b0' : ''}`
    : 'Stationary'
  return `<div class="hcfm-pop-h"><span>${esc(v.vessel_name || 'Vessel')}</span>${chip}</div>`
    + `<div class="hcfm-pop-b">Position <b>${fmtLat(v.lat)} ${fmtLon(v.lon)}</b><br>Status <b>${motion}</b>${v.ts ? '<br>AIS fix <b>' + ago(v.ts) + '</b>' : ''}</div>`
    + `<button class="hcfm-open" data-hcfm-open="${esc(v.id)}">Open vessel &rarr;</button>`
}

const makeIcon = v => L.divIcon({ className: 'hcfm-icon', html: markerHtml(v), iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -20] })

// vessels: [{ id, vessel_name, lat, lon, speed, course, ts, total }]
export default function FleetMap({ vessels = [], highlightId = null, focusId = null, onMarkerClick, onOpen, height = 380 }) {
  const mapRef = useRef(null)
  const containerRef = useRef(null)
  const markersRef = useRef({})
  const fitKeyRef = useRef('')
  const hlRef = useRef(highlightId)
  const onOpenRef = useRef(onOpen)
  const onMarkerRef = useRef(onMarkerClick)
  onOpenRef.current = onOpen
  onMarkerRef.current = onMarkerClick
  hlRef.current = highlightId

  function fitAll() {
    const map = mapRef.current
    const ms = Object.values(markersRef.current)
    if (!map || !ms.length) return
    if (ms.length === 1) map.setView(ms[0].getLatLng(), 9)
    else map.fitBounds(L.featureGroup(ms).getBounds(), { paddingTopLeft: [50, 50], paddingBottomRight: [170, 50], maxZoom: 9 })
  }
  // labels that would overlap (two boats close together, e.g. Antibes / La Ciotat at fleet zoom) move to the left, above or below
  function placeAll() {
    const map = mapRef.current
    if (!map) return
    const pts = Object.entries(markersRef.current).map(([id, m]) => {
      const el = m.getElement(), lab = el && el.querySelector('.hcfm-label'), pt = map.latLngToContainerPoint(m.getLatLng())
      return { id, x: pt.x, y: pt.y, w: lab ? lab.offsetWidth || 120 : 120, h: lab ? lab.offsetHeight || 40 : 40 }
    })
    const size = map.getSize(), sides = placeLabels(pts, { r: 17, gap: 8, width: size.x, height: size.y })
    Object.entries(markersRef.current).forEach(([id, m]) => {
      const el = m.getElement(); if (!el) return
      ;['right', 'left', 'up', 'down'].forEach(sd => el.classList.toggle('hcfm-side-' + sd, sides[id] === sd))
    })
  }
  function applyHighlight() {
    Object.entries(markersRef.current).forEach(([id, m]) => {
      const on = String(id) === String(hlRef.current)
      const el = m.getElement()
      if (el) el.classList.toggle('hcfm-hl', on)
      m.setZIndexOffset(on ? 1000 : 0)
    })
  }

  useEffect(() => {
    ensureCss()
    if (!containerRef.current || mapRef.current) return
    const map = L.map(containerRef.current, { scrollWheelZoom: false, worldCopyJump: true, minZoom: 2 }).setView([35, -20], 3)
    map.attributionControl.setPrefix(false)
    map.attributionControl.addAttribution(OSM_ATTRIBUTION)   // on every base layer (legal-app)
    const esri = 'https://server.arcgisonline.com/ArcGIS/rest/services/'
    const dark = L.layerGroup([
      L.tileLayer(esri + 'Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}', { maxZoom: 16, attribution: 'Tiles &copy; Esri' }),
      L.tileLayer(esri + 'Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}', { maxZoom: 16 }),
    ])
    const sat = L.tileLayer(esri + 'World_Imagery/MapServer/tile/{z}/{y}/{x}', { maxZoom: 19, attribution: 'Imagery &copy; Esri' })
    const seamarks = L.tileLayer('https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png', { minZoom: 9, maxZoom: 18, attribution: '&copy; OpenSeaMap' })
    dark.addTo(map)
    seamarks.addTo(map)
    L.control.layers({ Dark: dark, Satellite: sat }, { 'Sea marks': seamarks }, { position: 'topright' }).addTo(map)
    const Fit = L.Control.extend({
      options: { position: 'topleft' },
      onAdd() {
        const d = L.DomUtil.create('div', 'leaflet-bar')
        const b = L.DomUtil.create('button', 'hcfm-fit', d)
        b.title = 'Fit whole fleet'
        b.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>'
        L.DomEvent.disableClickPropagation(d)
        b.onclick = () => fitAll()
        return d
      },
    })
    new Fit().addTo(map)
    map.on('zoomend', () => placeAll())
    map.on('focus', () => map.scrollWheelZoom.enable())
    map.on('blur', () => map.scrollWheelZoom.disable())
    map.on('popupopen', e => {
      const el = e.popup.getElement()
      const b = el && el.querySelector('[data-hcfm-open]')
      if (b) b.onclick = () => onOpenRef.current && onOpenRef.current(b.getAttribute('data-hcfm-open'))
    })
    mapRef.current = map
    setTimeout(() => map.invalidateSize(), 0)
    // fullscreen button (Safari needs the webkit variant); Esc exits
    try {
      const FS = L.Control.extend({ options: { position: 'topleft' }, onAdd() {
        const b = L.DomUtil.create('a', 'leaflet-bar hc-fs-btn')
        b.href = '#'; b.title = 'Full screen'; b.innerHTML = '\u26f6'
        b.style.cssText = 'width:30px;height:30px;line-height:30px;text-align:center;font-size:16px;background:#fff;color:#0F2240;display:block;margin-top:6px;cursor:pointer'
        L.DomEvent.on(b, 'click', e => {
          L.DomEvent.preventDefault(e)
          const el = map.getContainer(), d = document
          const isFs = d.fullscreenElement || d.webkitFullscreenElement
          if (isFs) (d.exitFullscreen || d.webkitExitFullscreen).call(d)
          else (el.requestFullscreen || el.webkitRequestFullscreen).call(el)
        })
        return b
      } })
      map.addControl(new FS())
      const onFs = () => setTimeout(() => map.invalidateSize(), 150)
      document.addEventListener('fullscreenchange', onFs)
      document.addEventListener('webkitfullscreenchange', onFs)
    } catch (e) {}
    return () => { map.remove(); mapRef.current = null; markersRef.current = {}; fitKeyRef.current = '' }
  }, []) // eslint-disable-line

  const sig = JSON.stringify(vessels.map(v => [v.id, v.vessel_name, v.lat, v.lon, v.speed, v.course, v.ts, v.total]))
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const withPos = vessels.filter(v => typeof v.lat === 'number' && typeof v.lon === 'number')
    const keep = new Set(withPos.map(v => String(v.id)))
    Object.keys(markersRef.current).forEach(id => {
      if (!keep.has(id)) { map.removeLayer(markersRef.current[id]); delete markersRef.current[id] }
    })
    withPos.forEach(v => {
      const id = String(v.id)
      let m = markersRef.current[id]
      if (m) {
        m.setLatLng([v.lat, v.lon]); m.setIcon(makeIcon(v)); m.setPopupContent(popupHtml(v))
      } else {
        m = L.marker([v.lat, v.lon], { icon: makeIcon(v), riseOnHover: true }).addTo(map)
        m.bindPopup(popupHtml(v), { className: 'hcfm-popup', closeButton: false })
        m.on('click', () => onMarkerRef.current && onMarkerRef.current(v.id))
        markersRef.current[id] = m
      }
    })
    applyHighlight()
    const key = [...keep].sort().join(',')
    if (key && key !== fitKeyRef.current) { fitKeyRef.current = key; fitAll() }
    placeAll()
  }, [sig]) // eslint-disable-line

  useEffect(() => { applyHighlight() }, [highlightId]) // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const m = focusId != null ? markersRef.current[String(focusId)] : null
    if (m) map.flyTo(m.getLatLng(), 8, { duration: 0.8 })
    else if (focusId == null) fitAll()
  }, [focusId]) // eslint-disable-line

  return <div ref={containerRef} className="hcfm-map" style={{ height, width: '100%', borderRadius: 12, overflow: 'hidden' }} />
}
